// @flow strict

// $FlowFixMe[nonstrict-import]
import type {Query} from 'src/types/router';
import useI18n from 'src/hooks/useI18n';
// $FlowFixMe[nonstrict-import]
import type {EditorActionTypes} from './editor/reducer';

import * as React from 'react';
import logger from 'src/utils/logger';
import {useDispatch} from 'react-redux';
// $FlowFixMe[nonstrict-import]
import * as api from 'src/utils/redux-api';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
import {useHistory} from 'src/rerouter';
import HappyCalendar from 'src/images/scheduler/happy-calendar.svg';
import {
  Toast,
  ToastBody,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import classify from '@spaced-out/ui-design-system/lib/utils/classify';

import css from './auth-landing-page.css';


export const useSchedulerLandingPageWithCallback = (
  query: Query,
  onCompletion: () => mixed,
) => {
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();

  const t = labelI18nInstance.t;

  const ErrorToast = (
    <Toast semantic="danger">
      <ToastTitle>{t('Error', 'Error')}</ToastTitle>
      <ToastBody>{t('Something_went_wrong', 'Something went wrong')}</ToastBody>
    </Toast>
  );

  const findAndDeleteOldAccount = async () => {
    try {
      const accounts = await dispatch(api.get('scheduler/accounts/me'));
      const isConnected =
        accounts.length > 0 &&
        accounts[0].is_setup_complete &&
        accounts[0].is_synced;

      if (isConnected) {
        await dispatch(api.del('scheduler/accounts/auth'));
      }
    } catch (error) {
      // Log error but continue to post auth
      logger.error('Error during get/del auth:', error);
    }
  };

  React.useEffect(() => {
    const handleAuth = async () => {
      if (!query.code) {
        logger.error('error during dos-a-dos', query);
        try {
          await api.post('scheduler/accounts/auth/error', {
            error: query.error,
            reason: query.reason,
          });
        } catch (err) {
          logger.error('error posting auth error', err);
        }
        return toastApi.show(ErrorToast);
      }

      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone ??
        'America/Los_Angeles';

      try {
        await findAndDeleteOldAccount();

        // Always attempt to post auth, regardless of previous errors
        await dispatch(
          api.post('scheduler/accounts/auth', {code: query.code, timezone}),
        );

        onCompletion();
      } catch (err) {
        logger.error('error saving auth code', err);
      }
    };

    handleAuth();
  }, [query]);
};

function SchedulerContainer({
  isMobile,
  schedulerDispatch,
}: {
  isMobile: boolean,
  schedulerDispatch: (EditorActionTypes) => mixed,
}): React.Node {
  const router = useHistory();
  const {labelI18nInstance} = useI18n();
  const {changeLocation} = useWebAndExtRouterControl();
  const t = labelI18nInstance.t;
  const query = router.location.query;
  let isSourceConnectCalendar = false;

  if (query.state) {
    try {
      const state = JSON.parse(atob(query.state));
      if (state.source === 'connect_calendar') {
        isSourceConnectCalendar = true;
      }
    } catch (_error) {
      toastApi.show(
        <Toast semantic="danger">
          <ToastTitle>{t('Error', 'Error')}</ToastTitle>
          <ToastBody>
            {t('Something_went_wrong', 'Something went wrong')}
          </ToastBody>
        </Toast>,
      );
    }
  }

  useSchedulerLandingPageWithCallback(query, () => {
    schedulerDispatch({
      type: 'trigger_action',
      payload: {
        fetchAccounts: true,
        fetchMe: true,
      },
    });

    if (isMobile) {
      // note - any new route to mobile should be added here
      changeLocation(router, '/meetings/calendar-sync');
    } else {
      changeLocation(
        router,
        isSourceConnectCalendar
          ? '/meetings/connect-calendar?auth=ok'
          : '/meetings?auth=ok',
      );
    }
  });

  return <AuthLandingContent isMobile={isMobile} />;
}

export const AuthLandingContent = ({
  isMobile,
}: {
  isMobile?: boolean,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  return (
    <div
      className={classify(css.authSuccessWrapper, {
        [css.authSuccessWrapperMobile]: isMobile,
      })}
    >
      <div className={css.icon}>
        <HappyCalendar />
      </div>
      <h3>{`${t(
        'We_are_linking_your_account_right_now',
        'We are linking your account right now.',
      )}`}</h3>
      {!isMobile && (
        <p>{`${t(
          'You\'ll_be_redirected_to_the_scheduler_as_soon_as_we\'re_finished_so_you_can_begin_making_templates',
          'You\'ll be redirected to the scheduler as soon as we\'re finished so you can begin making templates',
        )}.`}</p>
      )}
    </div>
  );
};

export default SchedulerContainer;

export {HappyCalendar};

export const _HappyCalendar = (): React.Element<'svg'> => {
  return (
    <svg
      width="105"
      height="120"
      viewBox="0 0 105 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M43.2195 80.1302L43.2109 80.1194L43.2012 80.1096C41.7737 78.6757 39.3435 78.1532 37.6956 79.6431C36.0947 81.0904 35.7496 83.5305 37.2171 85.1743C41.0597 89.9573 46.8975 92.7336 53.0418 92.7336C59.3406 92.7336 65.0248 89.9553 69.0167 85.1761C70.4861 83.5323 70.1414 81.0909 68.5399 79.6431L68.5345 79.6382L68.5288 79.6337C66.9073 78.3306 64.48 78.4955 63.0253 80.1191L63.0204 80.1245L63.0159 80.1302C60.6351 83.1197 56.9126 84.915 53.0418 84.915C49.3245 84.915 45.6014 83.1209 43.2195 80.1302Z"
        fill="#2E855C"
        stroke="#2E855C"
        strokeWidth="0.5"
      />
      <path
        d="M40.8571 69.0082C43.5715 69.0082 45.9643 66.7656 45.9643 63.8791C45.9643 61.1501 43.5766 58.75 40.8571 58.75C37.9808 58.75 35.75 61.1552 35.75 63.8791C35.75 66.7606 37.9859 69.0082 40.8571 69.0082ZM65.1429 58.75C62.2666 58.75 60.0357 61.1552 60.0357 63.8791C60.0357 66.7606 62.2716 69.0082 65.1429 69.0082C67.8573 69.0082 70.25 66.7656 70.25 63.8791C70.25 61.1501 67.8623 58.75 65.1429 58.75Z"
        fill="#2E855C"
        stroke="#2E855C"
        strokeWidth="0.5"
      />
      <path
        d="M93.75 15H82.5V2.8125C82.5 1.40625 81.0938 0 79.6875 0H77.8125C76.1719 0 75 1.40625 75 2.8125V15H30V2.8125C30 1.40625 28.5938 0 27.1875 0H25.3125C23.6719 0 22.5 1.40625 22.5 2.8125V15H11.25C4.92188 15 0 20.1562 0 26.25V108.75C0 115.078 4.92188 120 11.25 120H93.75C99.8438 120 105 115.078 105 108.75V26.25C105 20.1562 99.8438 15 93.75 15ZM11.25 22.5H93.75C95.625 22.5 97.5 24.375 97.5 26.25V37.5H7.5V26.25C7.5 24.375 9.14062 22.5 11.25 22.5ZM93.75 112.5H11.25C9.14062 112.5 7.5 110.859 7.5 108.75V45H97.5V108.75C97.5 110.859 95.625 112.5 93.75 112.5Z"
        fill="#2E855C"
      />
    </svg>
  );
};

export const SadCalendar = (): React.Element<'svg'> => {
  return (
    <svg
      width="105"
      height="120"
      viewBox="0 0 105 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M37.502 91.3569L37.5074 91.3618L37.513 91.3663C39.1345 92.6694 41.5619 92.5045 43.0166 90.8809L43.0214 90.8755L43.0259 90.8698C45.4068 87.8803 49.1292 86.085 53 86.085C56.7174 86.085 60.4405 87.879 62.8223 90.8698L62.8309 90.8806L62.8407 90.8904C64.2681 92.3243 66.6983 92.8467 68.3462 91.3569C69.9471 89.9096 70.2922 87.4695 68.8247 85.8257C64.9821 81.0427 59.1443 78.2663 53 78.2663C46.7013 78.2663 41.0171 81.0446 37.0251 85.8239C35.5557 87.4677 35.9005 89.909 37.502 91.3569ZM40.8571 69.0082C43.5715 69.0082 45.9643 66.7656 45.9643 63.8791C45.9643 61.1501 43.5766 58.75 40.8571 58.75C37.9808 58.75 35.75 61.1552 35.75 63.8791C35.75 66.7606 37.9859 69.0082 40.8571 69.0082ZM65.1429 58.75C62.2666 58.75 60.0357 61.1552 60.0357 63.8791C60.0357 66.7606 62.2716 69.0082 65.1429 69.0082C67.8573 69.0082 70.25 66.7656 70.25 63.8791C70.25 61.1501 67.8623 58.75 65.1429 58.75Z"
        fill="#767676"
        stroke="#767676"
        strokeWidth="0.5"
      />
      <path
        d="M93.75 15H82.5V2.8125C82.5 1.40625 81.0938 0 79.6875 0H77.8125C76.1719 0 75 1.40625 75 2.8125V15H30V2.8125C30 1.40625 28.5938 0 27.1875 0H25.3125C23.6719 0 22.5 1.40625 22.5 2.8125V15H11.25C4.92188 15 0 20.1562 0 26.25V108.75C0 115.078 4.92188 120 11.25 120H93.75C99.8438 120 105 115.078 105 108.75V26.25C105 20.1562 99.8438 15 93.75 15ZM11.25 22.5H93.75C95.625 22.5 97.5 24.375 97.5 26.25V37.5H7.5V26.25C7.5 24.375 9.14062 22.5 11.25 22.5ZM93.75 112.5H11.25C9.14062 112.5 7.5 110.859 7.5 108.75V45H97.5V108.75C97.5 110.859 95.625 112.5 93.75 112.5Z"
        fill="#767676"
      />
    </svg>
  );
};
